import React, { useEffect, useRef, useState } from 'react';

export interface GammaDeck {
  generationId: string;
  gammaUrl: string;
  exportUrl?: string;
  exportFormat?: 'pdf' | 'pptx' | 'png';
  format: 'presentation' | 'document' | 'social' | 'webpage';
  numCards: number;
  title?: string;
  creditsUsed?: number;
  creditsRemaining?: number;
  durationSeconds: number;
  // Pre-wired for v1.6 PNG smoke test (see plan §2c). When exportAs=png, this
  // is set to the raw exportUrl; the card renders it best-effort as a single
  // <img>. If Gamma actually returns a ZIP, the <img> 404s and we fall back
  // gracefully to no preview.
  previewImageUrl?: string;
  previewImageUrls?: string[];  // populated only after ZIP extraction (v1.6)
}

export interface GammaProgress {
  generationId: string;
  status: 'pending' | 'completed' | 'failed';
  elapsedSeconds: number;
  message: string;
}

interface Props {
  deck?: GammaDeck;
  progress?: GammaProgress;
  /** When provided, the "..." menu surfaces an Archive option. */
  onArchive?: (gammaId: string) => void;
}

const formatLabel: Record<GammaDeck['format'], string> = {
  presentation: 'PRESENTATION',
  document: 'DOCUMENT',
  social: 'SOCIAL POST',
  webpage: 'WEBPAGE',
};

const GammaDeckCard: React.FC<Props> = ({ deck, progress, onArchive }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [archived, setArchived] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // In-flight state: show progress card.
  if (!deck && progress) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <span style={styles.gammaBadge}>GAMMA</span>
          <span style={styles.formatBadge}>GENERATING…</span>
        </div>
        <div style={styles.title}>{progress.message}</div>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${Math.min(95, 5 + progress.elapsedSeconds * 1.5)}%` }} />
        </div>
        <div style={styles.meta}>
          {progress.elapsedSeconds}s elapsed • Typical generation: 60–120s
        </div>
      </div>
    );
  }

  if (!deck) return null;

  const title = deck.title || 'Untitled deck';
  const formatTxt = formatLabel[deck.format] || deck.format.toUpperCase();
  const exportLabel = (deck.exportFormat || 'pdf').toUpperCase();

  const previewUrls =
    deck.previewImageUrls && deck.previewImageUrls.length > 0
      ? deck.previewImageUrls
      : deck.previewImageUrl
        ? [deck.previewImageUrl]
        : [];
  const showPreview = previewUrls.length > 0 && !previewFailed && !archived;

  const handleArchiveClick = () => {
    if (!onArchive) return;
    setMenuOpen(false);
    const ok = window.confirm(`Archive deck "${title}"? It will be removed from active workspace listings.`);
    if (!ok) return;
    onArchive(deck.generationId);
    setArchived(true);
  };

  return (
    <div style={{ ...styles.card, ...(archived ? styles.cardArchived : {}) }}>
      <div style={styles.header}>
        <span style={styles.gammaBadge}>GAMMA</span>
        <span style={styles.formatBadge}>
          {formatTxt} • {deck.numCards} cards
        </span>
        {archived && <span style={styles.archivedTag}>ARCHIVED ✓</span>}
        {onArchive && !archived && (
          <div style={styles.menuWrapper} ref={menuRef}>
            <button
              type="button"
              style={styles.menuButton}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Deck options"
            >
              ⋯
            </button>
            {menuOpen && (
              <div style={styles.menuPopover}>
                <button type="button" style={styles.menuItem} onClick={handleArchiveClick}>
                  Archive deck
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.title}>{title}</div>

      {showPreview && (
        <div style={styles.previewStrip}>
          {previewUrls.map((url, i) => (
            <a
              key={i}
              href={deck.gammaUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.previewLink}
            >
              <img
                src={url}
                alt={`Slide ${i + 1} preview`}
                style={styles.previewImg}
                onError={() => setPreviewFailed(true)}
              />
            </a>
          ))}
        </div>
      )}

      <div style={styles.meta}>
        Generated in {deck.durationSeconds}s
        {deck.creditsUsed != null && (
          <>
            {' • '}
            <span title="Gamma credits consumed by this generation">
              {deck.creditsUsed} credits used
            </span>
          </>
        )}
        {deck.creditsRemaining != null && (
          <>
            {' • '}
            <span style={styles.metaMuted}>{deck.creditsRemaining} remaining</span>
          </>
        )}
      </div>

      <div style={styles.actions}>
        <a
          href={deck.gammaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.primaryButton}
        >
          Open in Gamma to edit →
        </a>
        {deck.exportUrl && (
          <a
            href={deck.exportUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.secondaryButton}
          >
            Download {exportLabel}
          </a>
        )}
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  card: {
    marginTop: 12,
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(135, 95, 255, 0.06), rgba(255, 90, 180, 0.04))',
    border: '1px solid rgba(135, 95, 255, 0.25)',
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'all 220ms var(--ease-standard)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  gammaBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '2px 6px',
    borderRadius: 4,
    background: 'var(--cg-gradient-hero)',
    color: '#fff',
  },
  formatBadge: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--fg-2)',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--fg-1)',
    lineHeight: 1.3,
  },
  meta: {
    fontSize: 12,
    color: 'var(--fg-2)',
  },
  metaMuted: {
    color: 'var(--fg-3)',
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  primaryButton: {
    padding: '8px 14px',
    background: 'var(--cg-gradient-hero)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'opacity 200ms',
  },
  secondaryButton: {
    padding: '8px 14px',
    background: '#fff',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'all 200ms',
  },
  progressBar: {
    height: 4,
    background: 'rgba(135, 95, 255, 0.15)',
    borderRadius: 2,
    overflow: 'hidden',
    marginTop: 4,
  },
  progressFill: {
    height: '100%',
    background: 'var(--cg-gradient-hero)',
    transition: 'width 600ms ease-out',
  },
  cardArchived: {
    opacity: 0.55,
  },
  archivedTag: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '2px 6px',
    borderRadius: 4,
    background: 'rgba(140, 140, 140, 0.2)',
    color: 'var(--fg-2)',
    marginLeft: 'auto',
  },
  menuWrapper: {
    position: 'relative',
    marginLeft: 'auto',
  },
  menuButton: {
    background: 'transparent',
    border: 'none',
    color: 'var(--fg-2)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '0 6px',
    lineHeight: 1,
    fontFamily: 'var(--font-sans)',
  },
  menuPopover: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-md)',
    minWidth: 140,
    zIndex: 10,
    overflow: 'hidden',
  },
  menuItem: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    fontSize: 13,
    color: 'var(--fg-1)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  previewStrip: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  previewLink: {
    flex: '0 0 auto',
    textDecoration: 'none',
  },
  previewImg: {
    height: 100,
    maxWidth: 200,
    objectFit: 'cover',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-2)',
    display: 'block',
  },
};

export default GammaDeckCard;
